import { DocRoute } from '@squareup/dex-types-shared-docs';

import {
  isMdxEnabledForSlugFromPayload,
  type MdxFlagPayload,
} from './mdx-flag';
import type { Manifest } from './mdx-manifest';
import {
  buildPublishingContext,
  resolveManifestEntry,
} from './mdx-manifest-resolver';

interface DefaultProductionMdxDocRoutesOptions {
  now?: Date;
}

function resolveMdxManifestSlug(requestSlug: string): string {
  return requestSlug || 'homepage';
}

function resolveMdxPageSlug(requestSlug: string): string {
  return requestSlug || '/';
}

/**
 * Resolves manifest entries under the default production publishing context
 * (no query overrides) and returns doc-page routes for MDX-served slugs.
 */
function getDefaultProductionMdxDocRoutes(
  manifest: Manifest,
  options: DefaultProductionMdxDocRoutesOptions = {}
): DocRoute[] {
  const context = buildPublishingContext({
    manifest,
    ...(options.now !== undefined ? { now: options.now } : {}),
  });
  const routes: DocRoute[] = [];

  for (const publicSlug of Object.keys(manifest.entries)) {
    const resolved = resolveManifestEntry(manifest, publicSlug, context);
    if (resolved?.source === 'mdx') {
      routes.push({ type: 'doc-page', slug: publicSlug });
    }
  }

  return routes;
}

/**
 * Applies the same LaunchDarkly gate as runtime doc serving. A missing or
 * disabled payload fails closed so the sitemap does not advertise MDX-only
 * URLs that production would 404.
 */
function filterMdxDocRoutesByFlag(
  routes: DocRoute[],
  payload: MdxFlagPayload | null | undefined
): DocRoute[] {
  return routes.filter((route) =>
    isMdxEnabledForSlugFromPayload(route.slug, payload)
  );
}

/**
 * Merges Contentful route listings with MDX manifest routes. Contentful wins
 * on slug collisions; MDX fills gaps for slugs not yet in listDocRoutes.
 *
 * Contentful must keep winning during transition: roughly 60 collisions are
 * redirect-snapshot slugs that `precompile-mdx.ts` skips (see the
 * `contentfulDocSnapshot.redirects` guard), so runtime has no MDX payload and
 * falls through to the Contentful redirect. Listing those as `doc-page` URLs
 * would advertise pages production cannot serve until redirects are retired.
 */
function mergeDocRoutes(
  contentfulRoutes: DocRoute[],
  mdxRoutes: DocRoute[]
): DocRoute[] {
  const bySlug = new Map<string, DocRoute>();

  for (const route of contentfulRoutes) {
    bySlug.set(route.slug, route);
  }

  for (const route of mdxRoutes) {
    if (!bySlug.has(route.slug)) {
      bySlug.set(route.slug, route);
    }
  }

  return [...bySlug.values()];
}

export {
  filterMdxDocRoutesByFlag,
  getDefaultProductionMdxDocRoutes,
  mergeDocRoutes,
  resolveMdxManifestSlug,
  resolveMdxPageSlug,
};
export type { DefaultProductionMdxDocRoutesOptions };
